import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';
import {
  rolesApi,
  PermissionDto,
  RolePermissionDto,
} from '../api/rolesApi';
import { useLanguage } from '../i18n/LocalizationContext';

const EMPLOYEE_ALLOWED_PERMISSION_NAMES = new Set([
  'ViewEmployeeProfile',
  'ChangePassword',
  'ViewAttendance',
  'ViewAttendanceRecords',
  'CheckIn',
  'CheckOut',
  'ViewEmployeeDashboard',
  'ViewLeave',
  'ViewOwnReports',
  'ViewMySalary',
  'ViewMyPayslip',
  'ViewMyTaxDeclaration',
  'ViewOwnGeoLocation',
  'ViewNotifications',
]);

const isEmployeeRole = (role: RolePermissionDto | null) =>
  role?.roleName.trim().toLowerCase() === 'employee';

const canAssignPermissionToRole = (
  permission: PermissionDto,
  role: RolePermissionDto | null
) => !isEmployeeRole(role) || EMPLOYEE_ALLOWED_PERMISSION_NAMES.has(permission.permissionName);

const RoleManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const [roles, setRoles] = useState<RolePermissionDto[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionDto[]>([]);
  const [canManagePermissions, setCanManagePermissions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Role CRUD dialog
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RolePermissionDto | null>(null);
  const [viewOnlyRoleDialog, setViewOnlyRoleDialog] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [isSelectableInEmployeeProfile, setIsSelectableInEmployeeProfile] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRole, setDeletingRole] = useState<RolePermissionDto | null>(null);

  // Permission mapping dialog
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [mappingRole, setMappingRole] = useState<RolePermissionDto | null>(null);
  const [selectedPermIds, setSelectedPermIds] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const rolesData = await rolesApi.getRolesWithPermissions();
      setRoles(rolesData);

      try {
        const permsData = await rolesApi.getAllPermissions();
        setAllPermissions(permsData);
        setCanManagePermissions(true);
      } catch {
        setAllPermissions([]);
        setCanManagePermissions(false);
      }
    } catch {
      toast.error(t("legacy.Failed to load roles"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Group permissions by module
  const permissionsByModule = useMemo(() => {
    const map = new Map<string, PermissionDto[]>();
    allPermissions.forEach((p) => {
      const mod = p.module || 'General';
      if (!map.has(mod)) map.set(mod, []);
      map.get(mod)!.push(p);
    });
    return map;
  }, [allPermissions]);

  const filteredRoles = roles.filter(
    (r) =>
      !searchTerm ||
      r.roleName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isProtectedAdminRole = (role: RolePermissionDto) =>
    role.roleName.trim().toLowerCase() === 'admin';
  const isAdminRole = mappingRole ? isProtectedAdminRole(mappingRole) : false;
  const assignablePermissions = useMemo(
    () => allPermissions.filter((permission) => canAssignPermissionToRole(permission, mappingRole)),
    [allPermissions, mappingRole]
  );

  const canEditRole = (role: RolePermissionDto) => !isProtectedAdminRole(role);
  const canDeleteRole = (role: RolePermissionDto) => !isProtectedAdminRole(role);
  const canManageRolePermissions = (_role: RolePermissionDto) => canManagePermissions;
  const canViewRolePermissions = (role: RolePermissionDto) =>
    isProtectedAdminRole(role) && !canManagePermissions;
  const canOpenPermissionDialog = (role: RolePermissionDto) =>
    canManagePermissions || canViewRolePermissions(role);

  // ── Role CRUD ──────────────────────────────────────────
  const resetRoleForm = () => {
    setRoleName('');
    setRoleDescription('');
    setIsSelectableInEmployeeProfile(false);
    setEditingRole(null);
    setViewOnlyRoleDialog(false);
  };

  const openAddRole = () => {
    resetRoleForm();
    setRoleDialogOpen(true);
  };

  const openEditRole = (role: RolePermissionDto) => {
    const isViewOnly = !canEditRole(role);

    setEditingRole(role);
    setRoleName(role.roleName);
    setRoleDescription(role.description || '');
    setIsSelectableInEmployeeProfile(role.isSelectableInEmployeeProfile);
    setViewOnlyRoleDialog(isViewOnly);
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleName.trim()) {
      toast.warning(t("legacy.Role name is required"));
      return;
    }
    setSaving(true);
    try {
      const trimmedRoleName = roleName.trim();
      const trimmedDescription = roleDescription.trim();

      if (editingRole) {
        if (!canEditRole(editingRole)) {
          toast.warning(t("legacy.The default Admin role cannot be edited."));
          setSaving(false);
          return;
        }

        await rolesApi.updateRole(editingRole.roleId, {
          id: editingRole.roleId,
          roleName: trimmedRoleName,
          description: trimmedDescription,
          isActive: true,
          isSelectableInEmployeeProfile,
        });

        setRoles((prev) =>
          prev.map((role) =>
            role.roleId === editingRole.roleId
              ? {
                  ...role,
                  roleName: trimmedRoleName,
                  description: trimmedDescription,
                  isActive: true,
                  isSelectableInEmployeeProfile,
                }
              : role
          )
        );
        toast.success(t("legacy.Role updated"));
      } else {
        await rolesApi.createRole({
          roleName: trimmedRoleName,
          description: trimmedDescription,
          isSelectableInEmployeeProfile,
          permissionIds: [],
        });
        toast.success(t("legacy.Role created"));
      }
      setRoleDialogOpen(false);
      resetRoleForm();
      void loadData();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (editingRole ? t("legacy.Failed to update role") : t("legacy.Failed to create role"));
      toast.error(t(message));
    } finally {
      setSaving(false);
    }
  };

  const openDeleteRole = (role: RolePermissionDto) => {
    if (!canDeleteRole(role)) {
      toast.warning(t("legacy.The default Admin role cannot be deleted."));
      return;
    }

    setDeletingRole(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteRole = async () => {
    if (!deletingRole) return;
    setSaving(true);
    try {
      await rolesApi.deleteRole(deletingRole.roleId);
      toast.success(t("legacy.Role deleted"));
      setDeleteDialogOpen(false);
      setDeletingRole(null);
      await loadData();
    } catch {
      toast.error(t("legacy.Failed to delete role"));
    } finally {
      setSaving(false);
    }
  };

  // ── Permission Mapping ─────────────────────────────────
  const [viewOnlyPermDialog, setViewOnlyPermDialog] = useState(false);

  const openPermMapping = (role: RolePermissionDto) => {
    if (!canOpenPermissionDialog(role)) {
      toast.warning(t("legacy.You do not have permission to view or manage role permissions."));
      return;
    }

    const isViewOnly = !canManageRolePermissions(role);
    setMappingRole(role);
    setSelectedPermIds(
      new Set(
        role.permissions
          .filter((permission) => canAssignPermissionToRole(permission, role))
          .map((permission) => permission.id)
      )
    );
    setViewOnlyPermDialog(isViewOnly);
    // Expand all modules by default
    setExpandedModules(new Set(permissionsByModule.keys()));
    setPermDialogOpen(true);
  };

  const togglePermission = (permId: string) => {
    if (viewOnlyPermDialog) return;
    const permission = allPermissions.find((item) => item.id === permId);
    if (!permission || !canAssignPermissionToRole(permission, mappingRole)) return;

    setSelectedPermIds((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  };

  const toggleModule = (module: string) => {
    if (viewOnlyPermDialog) return;
    const modulePerms = (permissionsByModule.get(module) ?? []).filter((permission) =>
      canAssignPermissionToRole(permission, mappingRole)
    );
    if (modulePerms.length === 0) return;

    const allSelected = modulePerms.every((p) => selectedPermIds.has(p.id));
    setSelectedPermIds((prev) => {
      const next = new Set(prev);
      modulePerms.forEach((p) => {
        if (allSelected) next.delete(p.id);
        else next.add(p.id);
      });
      return next;
    });
  };

  const toggleModuleExpand = (module: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(module)) next.delete(module);
      else next.add(module);
      return next;
    });
  };

  const handleSavePermissions = async () => {
    if (!mappingRole || viewOnlyPermDialog) return;
    setSaving(true);
    try {
      const assignablePermissionIds = new Set(assignablePermissions.map((permission) => permission.id));
      await rolesApi.assignPermissions(mappingRole.roleId, {
        roleId: mappingRole.roleId,
        permissionIds: Array.from(selectedPermIds).filter((permissionId) =>
          assignablePermissionIds.has(permissionId)
        ),
      });
      toast.success(t("legacy.Permissions updated for {roleName}", { roleName: mappingRole.roleName }));
      setPermDialogOpen(false);
      setMappingRole(null);
      await loadData();
    } catch {
      toast.error(t("legacy.Failed to assign permissions"));
    } finally {
      setSaving(false);
    }
  };

  const selectAllPermissions = () => {
    setSelectedPermIds(new Set(assignablePermissions.map((p) => p.id)));
  };

  const clearAllPermissions = () => {
    setSelectedPermIds(new Set());
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          {t("settings.roleManagement")}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddRole} sx={{ textTransform: 'none' }}>
          {t("legacy.Add Role")}
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          size="small"
          placeholder={t("legacy.Search roles...")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280 }}
        />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("common.totalRecords", { count: filteredRoles.length })}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("legacy.Role Name")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("employee.profile")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("navigation.permissions")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  {t("common.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{t("legacy.No roles found")}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredRoles.map((role, idx) => (
                <TableRow key={role.roleId} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontWeight={600}>{role.roleName}</Typography>
                      {isProtectedAdminRole(role) && (
                        <Chip label={t("legacy.System")} size="small" color="info" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={role.isSelectableInEmployeeProfile ? 'success' : 'default'}
                      label={role.isSelectableInEmployeeProfile ? t("legacy.Shown") : t("legacy.Hidden")}
                      variant={role.isSelectableInEmployeeProfile ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {role.permissions.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {t("legacy.No permissions assigned")}
                        </Typography>
                      )}
                      {role.permissions.slice(0, 5).map((p) => (
                        <Chip key={p.id} label={t(p.permissionName)} size="small" variant="outlined" color="primary" />
                      ))}
                      {role.permissions.length > 5 && (
                        <Chip label={t("legacy.+{count} more", { count: role.permissions.length - 5 })} size="small" color="default" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="info"
                      title={
                        canManageRolePermissions(role)
                          ? t("settings.managePermissions")
                          : t("legacy.View permissions for Admin role")
                      }
                      onClick={() => openPermMapping(role)}
                      disabled={!canOpenPermissionDialog(role)}
                    >
                      <SecurityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      title={canEditRole(role) ? t("legacy.Edit Role") : t("legacy.View Role")
                      }
                      onClick={() => openEditRole(role)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      title={canDeleteRole(role) ? t("legacy.Delete Role") : t("legacy.Delete disabled for Admin role")}
                      onClick={() => openDeleteRole(role)}
                      disabled={!canDeleteRole(role)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {/* ── Role Create/Edit Dialog ── */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {viewOnlyRoleDialog ? t("legacy.View Role") : editingRole ? t("legacy.Edit Role") : t("legacy.Add Role")}
          <IconButton onClick={() => setRoleDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
            label={t("legacy.Role Name")}
            value={roleName}
            onChange={(e) => {
              const value = e.target.value;

              // Allow only alphabets and spaces + max 50 chars
              if (/^[A-Za-z ]*$/.test(value) && value.length <= 50) {
                setRoleName(value);
              }
            }}
            fullWidth
            required
            autoFocus
            inputProps={{ maxLength: 50 }}
            helperText={t("legacy.{count}/50 characters", { count: roleName.length })}
            error={roleName.length === 50}
            disabled={viewOnlyRoleDialog}
          />

            <TextField
              label={t("common.description")}
              value={roleDescription}
              onChange={(e) => {
                const value = e.target.value;

                if (value.length <= 200) {
                  setRoleDescription(value);
                }
              }}
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 200 }}
              helperText={t("legacy.{count}/200 characters", { count: roleDescription.length })}
              error={roleDescription.length === 200}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSelectableInEmployeeProfile}
                  onChange={(e) => setIsSelectableInEmployeeProfile(e.target.checked)}
                  disabled={viewOnlyRoleDialog}
                />
              }
              label={t("legacy.Show in Employee Profile")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t("common.cancel")}
          </Button>
          {!viewOnlyRoleDialog && (
            <Button variant="contained" onClick={handleSaveRole} disabled={saving} sx={{ textTransform: 'none' }}>
              {saving ? <CircularProgress size={20} /> : editingRole ? t("common.update") : t("common.create")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t("legacy.Confirm Delete")}
          <IconButton onClick={() => setDeleteDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t("legacy.Are you sure you want to delete role")} <strong>{deletingRole?.roleName}</strong>? {t("legacy.This will also remove all permission mappings and user assignments for this role.")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteRole} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={20} /> : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Permission Mapping Dialog ── */}
      <Dialog open={permDialogOpen} onClose={() => setPermDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {viewOnlyPermDialog ? t("legacy.View Permissions") : t("legacy.Assign Permissions")} - {mappingRole?.roleName}
          <IconButton onClick={() => setPermDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} mb={2}>
            <Button size="small" variant="outlined" onClick={selectAllPermissions} disabled={viewOnlyPermDialog} sx={{ textTransform: 'none' }}>
              {t("legacy.Select All")}
            </Button>
            <Button size="small" variant="outlined" color="secondary" onClick={clearAllPermissions} disabled={viewOnlyPermDialog} sx={{ textTransform: 'none' }}>
              {t("legacy.Clear All")}
            </Button>
            <Box flex={1} />
            <Typography variant="body2" color="text.secondary" alignSelf="center">
              {t("common.selectedCount", { selected: selectedPermIds.size, total: assignablePermissions.length })}
            </Typography>
          </Stack>
          {isEmployeeRole(mappingRole) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("legacy.Employee role is limited to self-service permissions only.")}
            </Typography>
          )}

          {/* {Array.from(permissionsByModule.entries()).map(([module, perms]) => {
            const allChecked = perms.every((p) => selectedPermIds.has(p.id));
            const someChecked = perms.some((p) => selectedPermIds.has(p.id));
            const isExpanded = expandedModules.has(module);

            return (
              <Paper key={module} variant="outlined" sx={{ mb: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ px: 2, py: 1, cursor: 'pointer', bgcolor: 'grey.50' }}
                  onClick={() => toggleModuleExpand(module)}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleModule(module);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                      />
                    }
                    label={
                      <Typography fontWeight={600}>
                        {module}{' '}
                        <Chip label={perms.length} size="small" sx={{ ml: 0.5 }} />
                      </Typography>
                    }
                    sx={{ flex: 1 }}
                  />
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Stack>
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 2, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {perms.map((perm) => (
                      <FormControlLabel
                        key={perm.id}
                        control={
                          <Checkbox
                            checked={selectedPermIds.has(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{perm.permissionName}</Typography>
                            {perm.description && (
                              <Typography variant="caption" color="text.secondary">
                                {perm.description}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{ width: '48%', mr: 0 }}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Paper>
            );
          })} */}

          {Array.from(permissionsByModule.entries()).map(([module, perms]) => {
  const visiblePerms = isEmployeeRole(mappingRole)
    ? perms.filter((permission) => canAssignPermissionToRole(permission, mappingRole))
    : perms;

  if (visiblePerms.length === 0) return null;

  const allChecked = visiblePerms.every((p) => selectedPermIds.has(p.id));
  const someChecked = visiblePerms.some((p) => selectedPermIds.has(p.id));
  const isExpanded = expandedModules.has(module);

  // ✅ Disable only the Settings module for Admin role
  const isSettingsModule = module.trim().toLowerCase() === 'settings';
  const isModuleDisabled = viewOnlyPermDialog || (isAdminRole && isSettingsModule);

  return (
    <Paper key={module} variant="outlined" sx={{ mb: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ px: 2, py: 1, cursor: 'pointer', bgcolor: 'grey.50' }}
        onClick={() => toggleModuleExpand(module)}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allChecked}
              indeterminate={!allChecked && someChecked}
              onChange={(e) => {
                e.stopPropagation();
                toggleModule(module);
              }}
              onClick={(e) => e.stopPropagation()}
              size="small"
              disabled={isModuleDisabled} // ✅
            />
          }
          label={
            <Typography fontWeight={600}>
              {t(module)}{' '}
              <Chip label={visiblePerms.length} size="small" sx={{ ml: 0.5 }} />
            </Typography>
          }
          sx={{ flex: 1 }}
        />
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Stack>
      <Collapse in={isExpanded}>
        <Box sx={{ px: 2, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {visiblePerms.map((perm) => (
            <FormControlLabel
              key={perm.id}
              control={
                <Checkbox
                  checked={selectedPermIds.has(perm.id)}
                  onChange={() => togglePermission(perm.id)}
                  size="small"
                  disabled={isModuleDisabled} // ✅
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t(perm.permissionName)}</Typography>
                  {perm.description && (
                    <Typography variant="caption" color="text.secondary">
                      {t(perm.description)}
                    </Typography>
                  )}
                </Box>
              }
              sx={{ width: '48%', mr: 0 }}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
})}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t("common.cancel")}
          </Button>
          {!viewOnlyPermDialog && (
            <Button variant="contained" onClick={handleSavePermissions} disabled={saving} sx={{ textTransform: 'none' }}>
              {saving ? <CircularProgress size={20} /> : t("settings.savePermissions")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagementPage;
